import React from 'react';
import { Clock, Calendar, Shirt, Table as TableIcon, Users } from 'lucide-react';
import { formatDateByTimezone, formatTime, formatSlotDuration, getTimezoneAbbreviation } from '@/lib/utils';
import { BookingCategoryImageCard } from '@/components/ui/BookingCategoryImageCard';

interface TableDetails {
  tableNumber?: string | null;
  tableSize?: number | null;
  bookingTime?: string | null;
  slotDuration?: number | null;
  isCustomSlotBooking?: boolean | null;
}

interface TransferBookingHeaderProps {
  /** Event name */
  eventName: string;
  /** Event description */
  eventDescription?: string | null;
  /** Event start time */
  startTime?: string | null;
  /** Event dress code */
  dressCode?: string | null;
  /** Event duration in minutes */
  eventDuration?: number | null;
  /** Booking category name (ticket/table type) */
  categoryName?: string;
  /** Booking category image ID */
  categoryImageId?: string | null;
  /** Whether this is a table booking */
  isTableBooking?: boolean;
  /** Table booking details */
  tableDetails?: TableDetails;
  /** Business timezone */
  businessTimeZone?: string;
  /** Click handler for category image card */
  onCategoryClick?: () => void;
}

/**
 * Header component for transfer booking page showing event and booking details.
 */
export const TransferBookingHeader: React.FC<TransferBookingHeaderProps> = ({
  eventName,
  eventDescription,
  startTime,
  dressCode,
  categoryName = 'General Admission',
  categoryImageId,
  isTableBooking,
  tableDetails,
  businessTimeZone,
  onCategoryClick,
}) => {
  // Check if we have table details to show
  const hasTableDetails = isTableBooking && (
    tableDetails?.tableNumber || 
    tableDetails?.tableSize || 
    tableDetails?.bookingTime
  );

  // Get category image URL
  const hasValidImage = categoryImageId && categoryImageId !== '00000000-0000-0000-0000-000000000000';
  const categoryImageUrl = hasValidImage ? categoryImageId : null;

  return (
    <>
      {/* Main Header Section */}
      <div className="flex items-start gap-6 mb-3">
        {/* Booking Category Image Card - Left */}
        <BookingCategoryImageCard
          imageId={categoryImageUrl}
          categoryName={categoryName}
          onClick={onCategoryClick}
          showLinkBadge={!!onCategoryClick}
        />

        {/* Event Details - Right */}
        <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">{eventName}</h3>
            {eventDescription && (
              <p className="text-sm text-gray-400 mb-4 line-clamp-2 leading-relaxed">
                {eventDescription}
              </p>
            )}
          </div>

          {/* Date, Time, Duration */}
          <div className="space-y-2">
            <div className="flex items-center gap-4 text-base">
              <div className="flex items-center gap-2 min-w-[170px]">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="font-bold text-gray-300">
                  {startTime && businessTimeZone ? `${formatDateByTimezone(startTime, businessTimeZone, { format: 'date', includeTimezone: false })} ${getTimezoneAbbreviation(startTime, businessTimeZone)}` : 'TBD'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="font-bold text-gray-300">
                  Entry Time: <span className="text-gray-300">{startTime ? formatTime(startTime, businessTimeZone, false) : 'TBD'}</span>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-base">
              {dressCode && (
                <div className="flex items-center gap-2 min-w-[170px]">
                  <Shirt className="w-4 h-4 text-gray-400" />
                  <span className="font-bold text-gray-300">{dressCode}</span>
                </div>
              )}
            </div>
          </div>

          {/* Fallback category name if no image */}
          {!hasValidImage && categoryName && (
            <p className="text-base text-gray-400 mt-2">{categoryName}</p>
          )}
        </div>
      </div>

      {/* Reservation Details Section - For table bookings */}
      {hasTableDetails && (
        <div className="mt-3 pt-3 border-t border-gray-700/30">
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Reservation Details
          </h4>
          <div className="flex flex-wrap items-center gap-4 text-base">
            {tableDetails?.tableNumber && (
              <div className="flex items-center gap-2">
                <TableIcon className="w-4 h-4 text-gray-400" />
                <span className="font-bold text-gray-300">
                  Table: <span className="text-gray-300">{tableDetails.tableNumber}</span>
                </span>
              </div>
            )}
            {tableDetails?.tableSize && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="font-bold text-gray-300">
                  Capacity: <span className="text-gray-300">{tableDetails.tableSize}</span>
                </span>
              </div>
            )}
            {tableDetails?.bookingTime && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="font-bold text-gray-300">
                  Reservation Time: <span className="text-gray-300">
                    {formatTime(tableDetails.bookingTime, businessTimeZone)}
                    {tableDetails?.isCustomSlotBooking && tableDetails?.slotDuration && tableDetails.slotDuration > 0 && (
                      <> (Duration: {formatSlotDuration(tableDetails.slotDuration)})</>
                    )}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TransferBookingHeader;
